/**
 * The "Declined Change Request" card of {@link ChangeRequestsTab} (CR-8718).
 *
 * The side whose request was declined used to be offered Propose Change and
 * Close Request in the PO Line pop-up's footer, away from the request itself.
 * Now Review Changes sends them here, where the two answers sit beside the
 * request they answer, as Accept / Reject do on the Active Change Request card:
 *
 *   - Acknowledge Decline: nothing on the line changes and the line goes back
 *     to the status it had before the request (WIP once the order is
 *     acknowledged). The server's `close-declined-request` (EXPEDITING-3).
 *   - Amend Request: the modal's Propose Change dialog, so the line moves to
 *     Changed and the usual change-request process carries on.
 *
 * The tab mounts this only for that side (mayCloseDeclinedRequest).
 */
import { useState } from 'react';
import { purchasingAPI, PurchaseOrderLine } from '../../lib/api';
import { useFormatters } from '../../contexts/PreferencesContext';
import { CheckCircle, Edit, Loader2, User, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { ACKNOWLEDGE_STATUS_CONFIG } from './lineStatus';
import { getStatusBadge } from './changeRequestsTabHelpers';
import type { ChangeRequestGroup } from './ChangeRequestCard';

interface DeclinedChangeRequestCardProps {
  line: PurchaseOrderLine;
  /** The newest declined request on the line; null when its rows carry no ref (pre-CR-4865). */
  group: ChangeRequestGroup | null;
  formatDate: (value: string) => string;
  formatDateTime: (value: string) => string;
  /** Amend Request: open the Propose Change dialog. */
  onAmendRequest: () => void;
  /** Reload the line once the decline is acknowledged. */
  onUpdate?: () => void | Promise<void>;
}

/** A request's value in the units of its field: dates and quantities as the line shows them. */
function useValueFormatter(group: ChangeRequestGroup | null, formatDate: (value: string) => string) {
  const { formatQuantity } = useFormatters();
  return (value: string) => {
    if (!value) return '—';
    if (group?.changeType === 'promise_date' || group?.changeType === 'required_date') return formatDate(value);
    if (group?.changeType === 'quantity') return formatQuantity(value);
    return value;
  };
}

export default function DeclinedChangeRequestCard({
  line, group, formatDate, formatDateTime, onAmendRequest, onUpdate,
}: DeclinedChangeRequestCardProps) {
  const [acknowledging, setAcknowledging] = useState(false);
  const formatValue = useValueFormatter(group, formatDate);
  const decision = group?.decision ?? null;

  const handleAcknowledge = async () => {
    setAcknowledging(true);
    try {
      const response = await purchasingAPI.closeDeclinedRequest(line.id);
      if (response.data.status === 'success') {
        const restored = ACKNOWLEDGE_STATUS_CONFIG[response.data.data?.acknowledge_status ?? '']?.label;
        toast.success(restored ? `Decline acknowledged. The line is back to ${restored}.` : 'Decline acknowledged');
        await onUpdate?.();
      } else {
        toast.error(response.data.message || 'Could not acknowledge the decline');
      }
    } catch (err: unknown) {
      console.error('Failed to acknowledge the decline:', err);
      toast.error(getApiErrorMessage(err, 'Could not acknowledge the decline'));
    } finally {
      setAcknowledging(false);
    }
  };

  return (
    <div className="border-2 border-red-200 rounded-lg bg-red-50/40 overflow-hidden" data-testid="declined-change-request">
      <div className="flex items-center justify-between px-4 py-3 bg-red-100/60 border-b border-red-200">
        <div className="flex items-center gap-2">
          <XCircle className="h-4 w-4 text-red-600" />
          <span className="font-semibold text-red-800">Declined Change Request</span>
        </div>
        {getStatusBadge('declined')}
      </div>
      <div className="p-4 space-y-3">
        {decision && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <User className="h-3.5 w-3.5" />
            <span>
              Declined by {decision.changed_by_company ? `${decision.changed_by_company} · ` : ''}{decision.changed_by}
              {decision.change_date && ` on ${formatDateTime(decision.change_date)}`}
            </span>
          </div>
        )}

        <div className="bg-white rounded-lg p-3 border text-sm space-y-1">
          {group ? (
            <>
              <div className="font-medium">{group.fieldLabel}</div>
              <div>
                <span className="text-muted-foreground">{formatValue(group.oldValue)}</span>
                <span className="mx-2 text-muted-foreground">&rarr;</span>
                <span className="font-semibold">{formatValue(group.newValue)}</span>
              </div>
              {decision?.reason && (
                <div className="text-muted-foreground">Comment: {decision.reason}</div>
              )}
            </>
          ) : (
            <div className="text-muted-foreground">Your change request was declined.</div>
          )}
        </div>

        <p className="text-sm text-muted-foreground">
          Acknowledge the decline to keep the line as it stands, or amend your request to propose a different value.
        </p>

        <div className="flex items-center gap-3 pt-2 border-t border-red-200">
          <button
            onClick={handleAcknowledge}
            disabled={acknowledging}
            title="Nothing on the line changes; it goes back to the status it had before the request."
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            {acknowledging ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4" />}
            Acknowledge Decline
          </button>
          <button
            onClick={onAmendRequest}
            disabled={acknowledging}
            title="Propose a different value. The line moves to Changed and waits on the other side."
            className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm font-medium disabled:opacity-50"
          >
            <Edit className="h-4 w-4" />
            Amend Request
          </button>
        </div>
      </div>
    </div>
  );
}
